/**
 * Permission System — Context-Aware Role Policies
 *
 * Replaces the simple boolean permission map with a policy-based system
 * that can evaluate permissions based on account relationships and context.
 *
 * Created as part of Account System Refactor — Fase 2, stap 2.11 (s83)
 *
 * Usage:
 *   canPerform(account, 'manage_classroom', { accounts, targetClassroomId: 'class_123' })
 *   canPerform(account, 'practice') // no context needed for simple permissions
 */

import type { Account, AccountRole, AccountsState } from '../types';
import { getLinkedLearners } from './queries';
import { getClassroomRole } from './classrooms';

// ── Types ────────────────────────────────────────────────────

/**
 * All permission actions in the system.
 * Grouped by domain for clarity.
 */
export type PermissionAction =
// Learner actions
'practice' | 'view_own_progress' | 'join_classroom' | 'view_own_data'
// Parent actions
| 'view_child_progress' | 'link_child' | 'export_child_data'
// Teacher actions
| 'manage_classroom' | 'create_classroom' | 'create_student' | 'view_learner' | 'remove_learner' | 'export_class_data'
// Wave 3.1 — co-teacher granular permissions
| 'manage_classroom_students' | 'delete_classroom' | 'manage_co_teachers'
// Aug 2026 — ontkoppelen is structureel en dus eigenaar-alleen, in tegenstelling
// tot `manage_classroom_students`. Stond als losse rolvergelijking in de shell.
| 'unlink_learner'
// Admin actions
| 'manage_accounts' | 'view_all_data' | 'manage_governance' | 'developer_tools'
// Wildcard
| '*';

/**
 * Context for evaluating conditional permissions.
 * Only provide the fields relevant to the permission being checked.
 */
export interface PermissionContext {
  accounts?: AccountsState;
  targetLearnerId?: string;
  targetClassroomId?: string;
  targetAccountId?: string;
}
interface PolicyRule {
  action: PermissionAction;
  condition: (account: Account, ctx?: PermissionContext) => boolean;
}

// ── Policy Definitions ───────────────────────────────────────

/**
 * Strict owner check — only true if the account is the registered teacherId
 * on the classroom. Co-teachers do NOT count.
 *
 * Wave 3.1: Previously this delegated to getTeacherClassrooms, which now
 * includes co-teacher classrooms. We must check ownership directly.
 */
function isClassroomOwner(account: Account, ctx?: PermissionContext): boolean {
  if (!ctx?.accounts || !ctx.targetClassroomId) return false;
  if (account.role !== 'teacher') return false;
  const classroom = ctx.accounts.classrooms[ctx.targetClassroomId];
  return classroom?.teacherId === account.id;
}

/**
 * Membership check — owner OR co-teacher of the classroom.
 */
function isClassroomMember(account: Account, ctx?: PermissionContext): boolean {
  if (!ctx?.accounts || !ctx.targetClassroomId) return false;
  if (account.role !== 'teacher') return false;
  return getClassroomRole(ctx.accounts, ctx.targetClassroomId, account.id) !== null;
}

/**
 * Verificatiepoort — spiegelt `is_teacher_of()` uit migratie 0020.
 *
 * De database houdt een niet-geverifieerde leerkracht bij élk leerlingpad weg.
 * Deze laag deed dat niet, en dat leverde de duurste soort inconsistentie op:
 * de UI liet hem een klas aanmaken en leerlingen toevoegen, waarna de database
 * alles tegenhield. De leerkracht zag een lege klas en geen enkele uitleg.
 *
 * ONBEKEND = TOEGESTAAN, en dat is bewust.
 * `teacherStatus` is optioneel. Een account dat alleen lokaal bestaat heeft de
 * kolom niet, en die mag niet stilvallen omdat er geen server is. Alleen een
 * EXPLICIETE `pending` of `rejected` blokkeert. Dit is geen beveiligingsgrens —
 * die staat in de database en kan niet omzeild worden — maar een eerlijke
 * weergave daarvan.
 */
function isVerifiedTeacher(account: Account): boolean {
  if (account.role !== 'teacher') return false;
  return account.teacherStatus !== 'pending' && account.teacherStatus !== 'rejected';
}

/**
 * Check if an account has a relationship with a target learner.
 */
function isLinkedToLearner(account: Account, ctx?: PermissionContext): boolean {
  if (!ctx?.accounts || !ctx.targetLearnerId) return false;
  if (account.role !== 'teacher' && account.role !== 'parent') return false;
  const linked = getLinkedLearners(ctx.accounts, account.id);
  return linked.some(l => l.id === ctx.targetLearnerId);
}
const ROLE_POLICIES: Record<AccountRole, PolicyRule[]> = {
  learner: [{
    action: 'practice',
    condition: () => true
  }, {
    action: 'view_own_progress',
    condition: () => true
  }, {
    action: 'view_own_data',
    condition: () => true
  }, {
    action: 'join_classroom',
    condition: () => true
  }],
  parent: [{
    action: 'view_child_progress',
    condition: isLinkedToLearner
  }, {
    action: 'link_child',
    condition: () => true
  }, {
    action: 'export_child_data',
    condition: isLinkedToLearner
  }],
  teacher: [
  // Owner-only structural actions
  {
    action: 'manage_classroom',
    condition: isClassroomOwner
  }, {
    action: 'delete_classroom',
    condition: isClassroomOwner
  }, {
    action: 'manage_co_teachers',
    condition: isClassroomOwner
  }, {
    /*
     Eigenaar-alleen, en die keuze is aug 2026 opnieuw bevestigd toen bleek dat
     de UI hem negeerde: `MyClassPage` liet ook een co-docent exporteren.

     De grond: een CSV verlaat élke toegangscontrole die we hebben. Geen RLS,
     niet in te trekken, geen spoor van wie hem opende, en hij overleeft het
     ontkoppelen van de co-docent die hem maakte. Dat is een verstrekking en
     geen dagelijkse begeleiding — het hoort bij wie de klas bezit.
     */
    action: 'export_class_data',
    condition: isClassroomOwner
  }, {
    /*
     ONTKOPPELEN is niet hetzelfde als uit de klas halen.

     Uit de klas halen (`manage_classroom_students`) verandert één lidmaatschap
     en is dagelijkse begeleiding — daar is een co-docent voor. Ontkoppelen
     verbreekt de band tussen leerkracht en leerling volledig, ook buiten deze
     klas, en is daarmee structureel. Dat hoort bij wie de klas bezit.

     Deze regel stond als losse `isOwner`-vergelijking in TeacherShellV2 en niet
     in deze tabel. Dat is hoe hij kon afwijken van de buurhandeling.
     */
    action: 'unlink_learner',
    condition: isClassroomOwner
  },
  // Owner-or-co-teacher operational actions
  {
    action: 'manage_classroom_students',
    condition: isClassroomMember
  },
  // Caller-scope actions — alleen voor een leerkracht die niet op verificatie
  // wacht. Zie `isVerifiedTeacher`: dit volgt 0020, het vervangt het niet.
  {
    action: 'create_classroom',
    condition: isVerifiedTeacher
  }, {
    action: 'create_student',
    condition: isVerifiedTeacher
  }, {
    action: 'view_learner',
    condition: isLinkedToLearner
  }, {
    action: 'remove_learner',
    condition: isLinkedToLearner
  }],
  admin: [{
    action: '*',
    condition: () => true
  }]
};

// ── Public API ───────────────────────────────────────────────

/**
 * Check if an account can perform a specific action.
 *
 * Demo accounts bypass all permission checks (always allowed).
 * Admin accounts have wildcard access to everything.
 *
 * @param account - The account attempting the action
 * @param action - The permission action to check
 * @param context - Optional context for conditional permissions
 * @returns true if the action is allowed
 *
 * @example
 * // Simple check (no context needed)
 * canPerform(learnerAccount, 'practice') // → true
 * canPerform(learnerAccount, 'manage_classroom') // → false
 *
 * @example
 * // Context-aware check
 * canPerform(teacherAccount, 'manage_classroom', {
 *   accounts: state,
 *   targetClassroomId: 'class_abc'
 * }) // → true only if teacher owns that classroom
 *
 * @example
 * // Parent viewing child
 * canPerform(parentAccount, 'view_child_progress', {
 *   accounts: state,
 *   targetLearnerId: 'learner_123'
 * }) // → true only if parent is linked to that learner
 */
export function canPerform(account: Account, action: PermissionAction, context?: PermissionContext): boolean {
  // VERWIJDERD: `if (account.isDemo) return true`.
  //
  // Die ene regel liet een demo-account élke controle passeren — ook
  // `isClassroomOwner` en `isLinkedToLearner`, dus ook toegang tot klassen en
  // kinderen die niet van hem waren. Vandaag is dat onschadelijk omdat niets
  // `canPerform()` aanroept, maar dat is precies wat het gevaarlijk maakt: de
  // dag dat iemand deze functie wél gaat gebruiken als autorisatiegrens, is de
  // bypass er stilletjes bij. Een demo-account is een gewoon account met
  // voorgevulde data; het hoort geen ander rechtenmodel te hebben.
  //
  // Moet demo-inhoud ergens zichtbaar zijn, dan is dat een UI-beslissing op
  // `account.isDemo` bij de aanroeper — niet een gat in de rechtencontrole.
  const policies = ROLE_POLICIES[account.role] || [];

  // Check for wildcard (admin)
  const wildcard = policies.find(p => p.action === '*');
  if (wildcard) return wildcard.condition(account, context);

  // Check for specific action
  const match = policies.find(p => p.action === action);
  return match ? match.condition(account, context) : false;
}

/**
 * Get all actions an account role is allowed to perform (without context).
 * Useful for UI: show/hide features based on role.
 */
export function getAllowedActions(role: AccountRole): PermissionAction[] {
  const policies = ROLE_POLICIES[role] || [];

  // Admin has wildcard — return all actions
  if (policies.some(p => p.action === '*')) {
    return Object.values(ROLE_POLICIES).flat().map(p => p.action).filter(a => a !== '*');
  }
  return policies.map(p => p.action);
}

/**
 * Check if a role has a specific permission (without context).
 * Simpler version for UI gating where context isn't available.
 */
export function roleHasPermission(role: AccountRole, action: PermissionAction): boolean {
  const policies = ROLE_POLICIES[role] || [];
  if (policies.some(p => p.action === '*')) return true;
  return policies.some(p => p.action === action);
}